
import Component from "vue-class-component";
import Vue from "vue";
import {
    MedicalCertificatesClient,
    MedicalInstitutionOption,
    MedicalPractitionerOption,
    PositionOption,
    PositionsClient,
    RestrictionOption,
    RestrictionsClient,
    ResultError,
    AddMedicalCertificateRequest,
    MedicalCertificateActions
} from "@/api-client";
import FileUploader from "@/components/FileUploader.vue";
import LabelOptional from "@/features/shared/components/LabelOptional.vue";
import TrueFalseOptions from "@/features/shared/TrueFalseOptions";
import Loader from "@/features/shared/components/Loader";
import {Prop} from "vue-property-decorator";
import FormLayout from "@/features/personProfile/components/FormLayout.vue";
import CheckboxInput from "@/features/shared/components/CheckboxInput.vue";
import toasts from "@/components/Toasts";
import userInfo from "@/features/shared/components/UserInfo";
import HeadingGroup from "@/features/shared/components/HeadingGroup.vue";
import Files from "@/components/Files.vue";
import moment from "moment";


@Component({
    components: {
        FileUploader,
        Files,
        FormLayout,
        HeadingGroup,
        LabelOptional,
        CheckboxInput
    }
})
export default class MedicalCertificateUpsertForm extends Vue {
    @Prop({required: true})
    model!: AddMedicalCertificateRequest;

    @Prop({required: true})
    seafarerId!: string;

    @Prop({required: true})
    submitCallback!: (model: AddMedicalCertificateRequest) => Promise<void>;

    @Prop({default: false})
    canAddFiles!: boolean;

    @Prop({required: true})
    canOmitFile!: boolean;

    @Prop({required: true})
    canChooseInstitutionAndPractitioner!: boolean;

    @Prop({required: true})
    actions!: MedicalCertificateActions;

    validationErrors = ResultError.createEmpty();
    isFitForServiceAtSea = this.model.verdict.isFitForServiceAtSea ?? true;
    positionId = this.model.verdict.positionId ?? null;

    hasRestrictions = this.model.verdict.hasRestrictions ?? false;
    restrictions: RestrictionOption[] = []; // Existing ones will be mapped in "created" lifecycle hook.

    medicalInstitutionOptions: MedicalInstitutionOption[] = [];
    medicalPractitionerOptions: MedicalPractitionerOption[] = [];
    restrictionOptions: RestrictionOption[] = [];
    positionOptions: PositionOption[] = [];
    yesNoOptions = TrueFalseOptions.options;

    fileLoader = new Loader();
    submitLoader = new Loader();

    isSeafarer = userInfo.seafarerId === this.seafarerId;
    isCreated = false;

    async created() {
        await Promise.all([
            new MedicalCertificatesClient().medicalInstitutionOptions().then(medicalInstitutionOptions => {
                this.medicalInstitutionOptions = medicalInstitutionOptions;
                this.medicalPractitionerOptions = this.medicalInstitutionOptions[0].medicalPractitionerOptions;

                if (this.model.medicalInstitutionId) {
                    this.medicalInstitutionOptions = this.medicalInstitutionOptions.filter(m => m.active || m.value == this.model.medicalInstitutionId);
                }
                else {
                    this.medicalInstitutionOptions = this.medicalInstitutionOptions.filter(m => m.active);
                    this.model.medicalInstitutionId = this.medicalInstitutionOptions[0].value;
                }

                if (this.model.medicalPractitionerId) {
                    const medicalPractitionerId = this.model.medicalPractitionerId;
                    this.resetPractitionerOptions(); // NOTE: This resets practitioner ID to first from the list.
                    this.model.medicalPractitionerId = medicalPractitionerId;
                } else {
                    this.resetPractitionerOptions();
                }
            }),
            new RestrictionsClient().list().then(restrictions => this.restrictionOptions = restrictions),
            new PositionsClient().activePositionOptions().then(positions => {
                this.positionOptions = positions;
            })
        ]);

        if (this.model.verdict.restrictions) {
            // Map existing restrictions.
            for (const restrictionId of this.model.verdict.restrictions) {
                const restrictionOption = this.restrictionOptions.find(x => x.value === restrictionId);
                if (!restrictionOption) {
                    console.error("Restriction option not found:");
                    console.error({restrictionIdLookedFor: restrictionId, allRestrictionOptions: restrictionOption})
                } else
                    this.restrictions.push(restrictionOption);
            }
        }
        this.isCreated = true;
        this.$watch(() => this.model, () => {
          if(this.model.issuedOn && moment().startOf("day").diff(moment(this.model.issuedOn).startOf("day")) < 0){
              this.validationErrors.clearKey("issuedOn");
              this.validationErrors.appendMessage(this.$ls.Global_IssuedOn_In_The_Future,"issuedOn");
          }
          else{
              this.validationErrors.clear();
          }
          if(this.model.issuedOn && this.model.validUntil){
              if(moment(this.model.validUntil).diff(this.model.issuedOn) <= 0){
                  this.validationErrors.clearKey("validUntil");
                  this.validationErrors.appendMessage(this.$ls.Global_ValidUntil_Before_IssuedOn,"validUntil");
              }
              else{
                  this.validationErrors.clearKey("validUntil");
              }
          }
      },{ deep: true });
    }

    async submit() {
      this.submitLoader.startLoading();
        this.validationErrors.clear();
        this.setIsFitForServiceAtSea();
        this.setRestrictions();
        await this.submitCallback(this.model)
            .then(async () => {
                toasts.success(this.$ls.Documents_Qualifications_Toast_Success_DocumentSaving);
            })
            .catch(error => this.setValidationErrors(error, this.$ls.Global_Toast_FormSaveFailure));
      this.submitLoader.endLoading();
    }

    private getRestrictionLabel(restriction: RestrictionOption) {
        return restriction.name.localizedOrDefault;
    }

    private removeRestriction(restriction: RestrictionOption) {
        this.restrictions.splice(this.restrictions.indexOf(restriction), 1);
    }
    private visionCheck(value:any) {
        if (this.model.issuedOn && this.model.vision.dateOfLastColorVisionTest) {
            if (moment(this.model.issuedOn).diff(this.model.vision.dateOfLastColorVisionTest) < 0) {
                this.validationErrors.clear();
                this.validationErrors.appendMessage(this.$ls.Global_dateOfLastColorVisionTest_After_IssuedOn,"vision.dateOfLastColorVisionTest");
                this.model.vision.dateOfLastColorVisionTest = value;
            } else {
                this.validationErrors.clear();
                this.model.vision.dateOfLastColorVisionTest = value;
            }
        }
    }
    private setRestrictions() {
        this.model.verdict.hasRestrictions = this.hasRestrictions;

        this.model.verdict.restrictions = [];
        if (this.hasRestrictions)
            this.model.verdict.restrictions = this.restrictions.map(x => x.value);
    }

    private resetPractitionerOptions() {
        if (!this.model.medicalInstitutionId) {
            return;
        }
        this.medicalPractitionerOptions = this.medicalInstitutionOptions.find(o =>
            o.value == this.model.medicalInstitutionId)!.medicalPractitionerOptions;
        this.model.medicalPractitionerId = this.medicalPractitionerOptions[0].value;
    }

    private setIsFitForServiceAtSea() {
        this.model.verdict.isFitForServiceAtSea = this.isFitForServiceAtSea;

        if (this.isFitForServiceAtSea) {
            this.model.verdict.positionId = this.positionId;
        } else {
            this.model.verdict.positionId = null;
        }
    }
}
